// PaidInteractionItem.jsx
import React, { useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import LazyImage, { Image } from '../component/LazyImage.jsx';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { ButtonId } from '../resource/mixpanel.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import getPag from '../resource/getPag.js';
import { QUICKPAY_BUNDLE_SECTION } from '../resource/shopConstants.js';

import useKaraokeTranslation from '../hook/useKaraokeTranslation.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import ToyIconSource from '../../img/ic_lovense_badge.svg';
import CommandIconSource from '../../img/ic_command.svg';

export const PaidInteractionItem = ({
  isNeedSendGiftConfirm = true,
  isAnimationGift = false,
  isPagGift = false,
  streamId = null,
  productId = null,
  isAuthed = false,
  hasPurchased = false,
  type = null,
  title = '',
  publicProductUrlPath = '',
  pagImage = null,
  duration = 0,
  price = null,
  hasEnoughBalance = false,
  notifySendGift = NULL_FUNCTION,
  sendGift = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
  openQuickpayModal = NULL_FUNCTION,
  addvalue = NULL_FUNCTION,
  fetchGift = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const isCommand = 'commands' === type;
  const isToy = 'toys' === type;
  const sectionId = isToy
    ? QUICKPAY_BUNDLE_SECTION.TOY_COMMAND
    : isCommand
      ? QUICKPAY_BUNDLE_SECTION.COMMAND
      : QUICKPAY_BUNDLE_SECTION.GIFT;

  const displayTitle = useKaraokeTranslation({
    productId,
    title,
    isToy: isToy,
  });

  // fetch gift data when price is empty
  useEffect(() => {
    if (productId && price == null) {
      fetchGift({ giftId: productId });
    }
  }, [fetchGift, price, productId]);

  const trackingPayload = useMemo(
    () => ({
      'stream.id': streamId,
      'product.id': productId,
      'product.price': price,
    }),
    [price, productId, streamId]
  );

  const handleClick = useCallback(async () => {
    if (!isAuthed || (!hasPurchased && !hasEnoughBalance)) {
      openQuickpayModal({
        sectionId,
      });
      return;
    }

    if (!hasEnoughBalance) {
      addvalue({
        via: isCommand ? 'karaoke' : 'gift',
      });
      return;
    }

    if (isPagGift) {
      const { isPAGSupported } = await getPag({
        imageSource: pagImage,
      });
      if (!isPAGSupported) {
        toggleModal({
          modalId: 'GiftAnimationFailedConfirmModal',
          isOpen: true,
          payload: {
            animationFailedGiftId: productId,
            isAnimationGift,
            isPagGift,
            isTooltip: true,
            streamId,
          },
        });
        return;
      }
    }

    if (isNeedSendGiftConfirm) {
      toggleModal({
        modalId: 'LivestreamGiftSendConfirmModal',
        isOpen: true,
        payload: {
          giftId: productId,
          streamId,
        },
      });
      return;
    }

    if (isAnimationGift || isPagGift || isToy || isCommand) {
      notifySendGift({
        streamId,
        giftId: productId,
        isTooltip: true,
      });
    } else {
      sendGift({
        streamId,
        giftId: productId,
        isTooltip: true,
      });
    }
  }, [
    addvalue,
    sectionId,
    hasEnoughBalance,
    hasPurchased,
    isAuthed,
    isAnimationGift,
    isCommand,
    isNeedSendGiftConfirm,
    isPagGift,
    isToy,
    notifySendGift,
    pagImage,
    productId,
    sendGift,
    streamId,
    toggleModal,
    openQuickpayModal,
  ]);

  const ProductImageComponent = useMemo(() => {
    if (isToy) return <IconImage src={ToyIconSource} alt="toy" />;

    if (isCommand) return <IconImage src={CommandIconSource} alt="command" />;

    const image = getResourceUrl({
      path: publicProductUrlPath,
      endpoint: `/${productId}.png`,
      resourceType: PUBLIC,
    }).href;

    const [avifSrcSet, webpSrcSet, pngSrcSet] = [
      MediaAssetFormat.AVIF,
      MediaAssetFormat.WEBP,
      MediaAssetFormat.PNG,
    ].map(format => getPublicSrcSet({ href: image, size: 32, format }));

    return (
      <LazyImage data-key="target" threshold={0.25}>
        <DecryptionWrapper resourceUrl={pngSrcSet.src}>
          <source srcSet={avifSrcSet.srcSet} type="image/avif" />
          <source srcSet={webpSrcSet.srcSet} type="image/webp" />
          <Image {...pngSrcSet} />
        </DecryptionWrapper>
      </LazyImage>
    );
  }, [isCommand, isToy, productId, publicProductUrlPath]);

  return (
    <StyledPaidInteractionItem
      data-element_id={ButtonId.LiveStream.ButtonHotkey}
      data-tracking_payload={trackingPayload}
      onClick={handleClick}
    >
      <ProductImageContainer>{ProductImageComponent}</ProductImageContainer>
      {isCommand || isToy
        ? `${displayTitle} ${t('command_sec', {
            number: duration,
            ns: TranslationNamespace.GIFT,
          })}`
        : displayTitle}
    </StyledPaidInteractionItem>
  );
};

PaidInteractionItem.propTypes = {
  isNeedSendGiftConfirm: PropTypes.bool,
  isAnimationGift: PropTypes.bool,
  isPagGift: PropTypes.bool,
  streamId: PropTypes.string,
  productId: PropTypes.string,
  isAuthed: PropTypes.bool,
  hasPurchased: PropTypes.bool,
  type: PropTypes.oneOf(['toys', 'commands', 'gifts']),
  title: PropTypes.string,
  publicProductUrlPath: PropTypes.string,
  pagImage: PropTypes.string,
  duration: PropTypes.number,
  price: PropTypes.number,
  hasEnoughBalance: PropTypes.bool,
  notifySendGift: PropTypes.func,
  sendGift: PropTypes.func,
  toggleModal: PropTypes.func,
  openQuickpayModal: PropTypes.func,
  addvalue: PropTypes.func,
  fetchGift: PropTypes.func,
};

const StyledPaidInteractionItem = styled.button`
  ${ResetButtonStyle};

  border-radius: 40px;
  padding: 2px 8px;

  display: flex;
  align-items: center;

  font-weight: 600;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral['100']};

  background-color: ${color.neutral['100_alpha_20']};
`;

const ProductImageContainer = styled.div`
  margin-right: 4px;
  width: 14px;
  height: 14px;
`;

const IconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export default PaidInteractionItem;
